import { useCallback, useEffect, useRef } from 'react';
import type { SeenBatch } from '../api/whatsNew';

/** What a drawn card or row calls once it is on screen and new. */
export type MarkShown = (kind: 'announcement' | 'event', id: string) => void;

/**
 * Says "I have been shown these" for whatever a screen actually drew — TROOP-268.
 *
 * **Seen means shown**, the meaning a thread's read marker has: a card is seen once it has been on
 * this person's screen, so the call comes from the card's own mount rather than from the list it
 * belongs to. A card behind *Show N more* has not mounted and is not marked; tapping the control
 * mounts it, and that marks it. Nothing has to be tapped to clear — an announcement's whole body is
 * on its card, so there would be nothing to tap.
 *
 * **The marker stays for the visit that drew it.** Nothing here touches the rows on screen; the
 * server simply stops calling them new, so they are gone on the next load — the same way a thread's
 * unread divider behaves.
 *
 * Only the noticeboard, the agenda and an event's own page use this. **Never the calendar or a
 * search**, which report `isNew` but must not clear it: looking something up is not being shown
 * what is new on the page you came for.
 *
 * Batched into one request per render pass, deduplicated for the life of the screen (StrictMode
 * mounts every effect twice in development), and silent on failure: a mark that does not land
 * leaves the marker for one more visit, which is not worth an error banner.
 */
export function useMarkSeen(post: (batch: SeenBatch) => Promise<void>): MarkShown {
  const sent = useRef(new Set<string>());
  const pending = useRef<SeenBatch>({ announcements: [], events: [] });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const poster = useRef(post);

  useEffect(() => {
    poster.current = post;
  }, [post]);

  const flush = useCallback(() => {
    timer.current = null;
    const batch = pending.current;

    if (batch.announcements.length === 0 && batch.events.length === 0) return;

    pending.current = { announcements: [], events: [] };
    poster.current(batch).catch(() => undefined);
  }, []);

  // Whatever was drawn just before the screen went away still counts as drawn.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      flush();
    },
    [flush],
  );

  return useCallback(
    (kind, id) => {
      const key = `${kind}:${id}`;
      if (sent.current.has(key)) return;
      sent.current.add(key);

      if (kind === 'announcement') pending.current.announcements.push(id);
      else pending.current.events.push(id);

      if (!timer.current) timer.current = setTimeout(flush, 0);
    },
    [flush],
  );
}
